import React, { useState } from 'react';
import Toast from 'react-bootstrap/Toast';
import ToastContainer from 'react-bootstrap/ToastContainer';
import './AddressDisplay.css';
import { AiOutlineCopy, AiOutlineCheck } from "react-icons/ai";
import bsc from '../../assets/bsc.png';
import eth from '../../assets/eth.png';
import sol from '../../assets/sol.png';
import arbitrum from '../../assets/arbitrum.svg';
import base from '../../assets/base.svg';
import polygon from '../../assets/polygon.svg';
import avax from '../../assets/avax.webp';
import robin from '../../assets/robin.svg';
import { RxExternalLink } from "react-icons/rx";

const External = RxExternalLink as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const CopyIcon = AiOutlineCopy as unknown as React.FC<React.SVGProps<SVGSVGElement>>;
const CheckIcon = AiOutlineCheck as unknown as React.FC<React.SVGProps<SVGSVGElement>>;

const shortenAddress = (address: string): string =>
  `${address.slice(0, 5)}...${address.slice(-4)}`;

interface AddressDisplayProps {
  address: string;
  chainId?: number;
}

const chainIconMapping: { [key: number]: string } = {
  1: eth,
  56: bsc,
  137: polygon,
  42161: arbitrum,
  8453: base, 
  501424: sol,
  43114: avax,
  4663: robin
};

const AddressDisplay: React.FC<AddressDisplayProps> = ({ address, chainId }) => {
  const [toastShow, setToastShow] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [copied, setCopied] = useState(false);

  // Builds the URL for the blockchain explorer according to the current chain
  const formatScanner = (address: string): string => {
    if (!chainId) return '#';
    switch (chainId) {
      case 1:
        return `https://etherscan.io/address/${address}`;
      case 56:
        return `https://bscscan.com/address/${address}`;
      case 137:
        return `https://polygonscan.com/address/${address}`;
      case 42161:
        return `https://arbiscan.io/address/${address}`;
      case 8453:
        return `https://basescan.org/address/${address}`;
      case 501424:
        return `https://solscan.io/account/${address}`;
      case 43114:
        return `https://snowtrace.io/address/${address}`;
      case 4663:
        return `https://robinhoodchain.blockscout.com/address/${address}`;
      default:
        return '#';
    }
  };

  const copyToClipboard = (text: string) => {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard
        .writeText(text)
        .then(() => {
          setToastMessage('Copied to clipboard!');
          setToastShow(true);
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        })
        .catch((err) => {
          console.error('Failed to copy using Clipboard API: ', err);
          setToastMessage('Failed to copy!');
          setToastShow(true);
        });
    } else {
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      try {
        const successful = document.execCommand('copy');
        if (successful) {
          setToastMessage('Copied to clipboard!');
          setToastShow(true);
          setCopied(true);
          setTimeout(() => setCopied(false), 3000);
        } else {
          setToastMessage('Failed to copy!');
          setToastShow(true);
        }
      } catch (err) {
        console.error('Fallback: Oops, unable to copy', err);
        setToastMessage('Failed to copy!');
        setToastShow(true);
      }
      document.body.removeChild(textarea);
    }
  };

  return (
    <>
      <div
        style={{
          cursor: 'pointer',
          display: 'inline-flex',
          alignItems: 'center',
        }}
        className='address-text'
      >
        {chainId != null && chainIconMapping[chainId] && (
          <img
            src={chainIconMapping[chainId]}
            alt={`chain-${chainId}`}
            style={{ width: '15px', height: '15px', marginRight: '5px' }}
          />
        )}
        <div onClick={() => copyToClipboard(address)}>
          {address && shortenAddress(address)}
        </div>
        {copied ? (
          <CheckIcon style={{ marginLeft: '5px', color: 'rgb(14, 203, 129)' }} />
        ) : (
          <CopyIcon onClick={() => copyToClipboard(address)} style={{ marginLeft: '5px' }} />
        )}
        {/* External icon wrapped in a link */}
        {chainId && (
          <a
          href={formatScanner(address)}
          target="_blank"
          rel="noopener noreferrer"
          style={{ marginLeft: '2px' }}
          className='externallink'
        >
          <External />
        </a>
        )}
      </div>
      <ToastContainer position="top-end" className="p-3">
        <Toast onClose={() => setToastShow(false)} show={toastShow} delay={3000} autohide>
          <Toast.Body>{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default AddressDisplay;
